import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { GrowthCharts } from "../components/GrowthCharts";
import { SpecPhaseDonut } from "../components/SpecPhaseDonut";
import { FunnelStrip, type FunnelStage } from "../components/FunnelStrip";

// The memex detail (spec-3): the SAME shape as the org view, scoped to one memex
// — cumulative specs over time, the activation funnel (the memex-scoped subset:
// no signup/user-growth stage), the current spec-phase donut + counts, and the
// underlying specs / decisions / ACs. A memex can also be marked internal (so a
// personal, org-less memex can be hidden from the board).
interface MemexDetail {
  id: string;
  name: string;
  ownerName: string | null;
  isInternal: boolean;
  mcpToolFired: boolean;
  testDataReceived: boolean;
  funnelDates: Record<string, string | null>;
  specs: { handle: string; title: string; status: string }[];
  decisions: { title: string; status: string }[];
  acs: { statement: string; kind: string; status: string }[];
}

const PHASE_ORDER = ["draft", "specify", "build", "verify", "done"] as const;

// Memex-scoped funnel: the org funnel minus "signed up" (a memex is past that by
// definition) and "MCP connected" (a session is user-level, not memex-level).
const MEMEX_FUNNEL_STAGES: FunnelStage[] = [
  { key: "firstSpec", label: "First spec", hint: "This memex has at least one spec." },
  { key: "decisionResolved", label: "Decision resolved", hint: "At least one decision resolved in this memex." },
  { key: "acDefined", label: "AC defined", hint: "At least one acceptance criterion defined in this memex." },
  { key: "mcpToolFired", label: "MCP tool fired", hint: "An MCP tool call has fired in this memex." },
  { key: "testDataReceived", label: "AC test data received", hint: "An acceptance criterion has received real test telemetry." },
  { key: "specInVerify", label: "First spec in Verify", hint: "A spec has reached the verify phase." },
  { key: "specInDone", label: "First spec in Done", hint: "A spec has reached done." },
];

export function MemexDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [memex, setMemex] = useState<MemexDetail | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "notfound" | "error">("loading");

  useEffect(() => {
    if (!id) return;
    api<{ memex: MemexDetail }>(`/api/memexes/${id}`)
      .then((r) => {
        setMemex(r.memex);
        setStatus("ok");
      })
      .catch((e) => setStatus(e?.status === 404 ? "notfound" : "error"));
  }, [id]);

  const phases = useMemo(() => {
    const p: Record<(typeof PHASE_ORDER)[number], number> = { draft: 0, specify: 0, build: 0, verify: 0, done: 0 };
    memex?.specs.forEach((s) => {
      if ((PHASE_ORDER as readonly string[]).includes(s.status)) p[s.status as (typeof PHASE_ORDER)[number]]++;
    });
    return p;
  }, [memex]);

  const funnel = {
    firstSpec: (memex?.specs.length ?? 0) > 0,
    decisionResolved: !!memex?.decisions.some((d) => d.status === "resolved"),
    acDefined: (memex?.acs.length ?? 0) > 0,
    mcpToolFired: !!memex?.mcpToolFired,
    testDataReceived: !!memex?.testDataReceived,
    specInVerify: !!memex?.specs.some((s) => s.status === "verify" || s.status === "done"),
    specInDone: !!memex?.specs.some((s) => s.status === "done"),
  };

  async function toggleInternal() {
    if (!id || !memex) return;
    const next = !memex.isInternal;
    await api(`/api/memexes/${id}/classification`, { method: "PUT", body: JSON.stringify({ isInternal: next }) });
    setMemex({ ...memex, isInternal: next });
  }

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/" className="text-sm text-muted hover:text-primary">
        &larr; Dashboard
      </Link>

      {status === "loading" && <p className="mt-6 text-muted">Loading…</p>}
      {status === "notfound" && <p className="mt-6 text-status-danger-text">Memex not found.</p>}
      {status === "error" && <p className="mt-6 text-status-danger-text">Failed to load.</p>}

      {status === "ok" && memex && (
        <>
          <header className="mt-4 mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-2xl text-heading" data-testid="memex-name">{memex.name}</h1>
              <p className="text-sm text-muted">{memex.ownerName ?? "—"}</p>
            </div>
            <button
              onClick={toggleInternal}
              data-testid="claim-internal"
              title="Mark this memex internal (e.g. a personal/throwaway one) so the 'Hide internal' filter drops it. Stored only in Backstage."
              className="rounded-lg border border-edge px-3 py-1.5 text-sm text-secondary hover:bg-card-hover"
            >
              {memex.isInternal ? "Unmark internal" : "Mark internal"}
            </button>
          </header>

          <div className="mb-6">
            <GrowthCharts query={`memex=${id}`} variant="memex" />
          </div>

          {/* Left column: donut + phase card-lets + count cards. Right column:
              the vertical activation funnel beside the donut. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section data-testid="phase-distribution">
              <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">Specs by phase</h2>
              <SpecPhaseDonut phases={phases} />
              <div className="mt-3 grid grid-cols-5 gap-2">
                {PHASE_ORDER.map((p) => (
                  <div key={p} className="rounded-lg border border-edge bg-surface px-2 py-2 text-center">
                    <div className="text-lg font-semibold text-heading" data-testid={`phase-${p}`}>{phases[p]}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted">{p}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <Stat label="specs" value={memex.specs.length} />
                <Stat label="decisions" value={memex.decisions.length} />
                <Stat label="ACs" value={memex.acs.length} />
              </div>
            </section>

            <FunnelStrip
              stages={MEMEX_FUNNEL_STAGES}
              funnel={funnel}
              dates={memex.funnelDates}
              hint="The memex-scoped activation funnel — the org funnel minus the user/signup stage, which doesn't apply to a single memex. The date is when each was first reached."
            />
          </div>

          <Section title={`Specs (${memex.specs.length})`}>
            {memex.specs.map((s) => (
              <div key={s.handle} className="flex items-center justify-between border-b border-edge-subtle px-4 py-2.5 last:border-0">
                <span className="font-medium text-primary">{s.title}</span>
                <Pill text={s.status} />
              </div>
            ))}
          </Section>

          <Section title={`Decisions (${memex.decisions.length})`}>
            {memex.decisions.map((d, i) => (
              <div key={i} className="flex items-center justify-between border-b border-edge-subtle px-4 py-2.5 last:border-0">
                <span className="text-primary">{d.title}</span>
                <span className="text-xs text-muted">{d.status}</span>
              </div>
            ))}
          </Section>

          <Section title={`Acceptance criteria (${memex.acs.length})`}>
            {memex.acs.map((a, i) => (
              <div key={i} className="flex items-center justify-between border-b border-edge-subtle px-4 py-2.5 last:border-0">
                <span className="text-primary">{a.statement}</span>
                <span className="text-xs text-muted">{a.kind}</span>
              </div>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-edge bg-surface px-4 py-2.5">
      <div className="text-2xl font-semibold text-heading" data-testid={`stat-${label.split(" ")[0]}`}>{value}</div>
      <div className="mt-0.5 text-xs uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">{title}</h2>
      <div className="overflow-hidden rounded-xl border border-edge">{children}</div>
    </section>
  );
}

function Pill({ text }: { text: string }) {
  return (
    <span className="rounded-full border border-edge bg-card-hover px-2 py-0.5 text-xs text-secondary">{text}</span>
  );
}
